import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, formatDistanceToNow } from 'date-fns';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { apiFetch } from '../services/httpClient';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Auth is httpOnly-cookie based (see AuthContext/api.js) — the browser sends
// the session cookie automatically on same-origin requests. Only attach an
// Authorization header if a legacy localStorage token actually exists;
// sending a synthesized "Bearer null" would make the server reject the
// request even though the valid cookie would otherwise authenticate it.
const getAuthToken = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return token && token !== 'null' && token !== 'undefined' ? token : null;
};

// Helper to get auth headers
const getAuthHeaders = (includeJsonContentType = false) => {
  const token = getAuthToken();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

// Fetch tier upgrades
const fetchTierUpgrades = async ({ page, limit, status }) => {
  const params = new URLSearchParams({ page, limit });
  if (status) params.append('status', status);

  return apiFetch(
    `${API_BASE}/api/tier-upgrades?${params}`,
    { headers: getAuthHeaders() },
    { fallbackMessage: 'Failed to load tier upgrades', suppressGlobalError: true }
  );
};

// Fetch companies for autocomplete
// Filters only Free and Professional tier companies (since only these can be upgraded)
// If search is provided, also filters by search term
const fetchCompanies = async (search = '') => {
  const params = new URLSearchParams({
    limit: '50',
    tier: 'free,professional'  // Only show upgradable tiers
  });
  if (search && search.trim()) {
    params.append('search', search.trim());
  }
  return apiFetch(
    `${API_BASE}/api/companies?${params}`,
    { headers: getAuthHeaders() },
    { fallbackMessage: 'Failed to load companies', suppressGlobalError: true }
  );
};

// Create tier upgrade
const createTierUpgrade = async (data) => {
  return apiFetch(
    `${API_BASE}/api/tier-upgrades`,
    {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify(data)
    },
    { fallbackMessage: 'Failed to grant temporary tier upgrade', suppressGlobalError: true }
  );
};

// Cancel tier upgrade
const cancelTierUpgrade = async ({ id, reason }) => {
  return apiFetch(
    `${API_BASE}/api/tier-upgrades/${id}/cancel`,
    {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ reason })
    },
    { fallbackMessage: 'Failed to cancel tier upgrade', suppressGlobalError: true }
  );
};

// Extend tier upgrade
const extendTierUpgrade = async ({ id, additionalDays }) => {
  return apiFetch(
    `${API_BASE}/api/tier-upgrades/${id}/extend`,
    {
      method: 'POST',
      headers: getAuthHeaders(true),
      body: JSON.stringify({ additionalDays })
    },
    { fallbackMessage: 'Failed to extend tier upgrade', suppressGlobalError: true }
  );
};

// Fetch stats
const fetchStats = async () => {
  return apiFetch(
    `${API_BASE}/api/tier-upgrades/stats/summary`,
    { headers: getAuthHeaders() },
    { fallbackMessage: 'Failed to load tier upgrade statistics', suppressGlobalError: true }
  );
};

const TierUpgrades = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  
  // Form state
  const [formData, setFormData] = useState({
    companyId: '',
    companyName: '',
    upgradedTier: 'professional',
    durationDays: 30,
    reason: '',
  });
  const [cancelReason, setCancelReason] = useState('');
  const [extendDays, setExtendDays] = useState(7);

  const { data, isLoading, error, refetch } = useQuery(
    ['tierUpgrades', page + 1, rowsPerPage, statusFilter],
    () => fetchTierUpgrades({ page: page + 1, limit: rowsPerPage, status: statusFilter }),
    {
      keepPreviousData: true,
      onError: (queryError) => {
        setFeedback({
          type: 'error',
          message: queryError?.message || 'Failed to load tier upgrades'
        });
      }
    }
  );

  const { data: statsData } = useQuery('tierUpgradeStats', fetchStats);

  // Company search - load all companies initially, then filter on search
  // Using enabled: createDialogOpen ensures the query runs when dialog opens
  const { data: companyData, isLoading: isSearchingCompanies, refetch: refetchCompanies } = useQuery(
    ['companySearch', companySearch],
    () => fetchCompanies(companySearch),
    {
      enabled: createDialogOpen, // Query runs when dialog is open
      staleTime: 30000, // Cache for 30 seconds to avoid unnecessary refetches
      onError: (error) => {
        console.error('❌ [TIER_UPGRADES] Failed to load companies:', error);
        setFeedback({
          type: 'error',
          message: error?.message || 'Failed to load companies for selection'
        });
      }
    }
  );

  // Update company options when data changes
  useEffect(() => {
    if (companyData?.companies) {
      console.log('✅ [TIER_UPGRADES] Companies loaded:', companyData.companies.length);
      setCompanyOptions(companyData.companies);
    }
  }, [companyData]);

  // Reset search when dialog opens
  useEffect(() => {
    if (createDialogOpen) {
      console.log('📊 [TIER_UPGRADES] Dialog opened, loading companies...');
      setCompanySearch(''); // Reset search
    } else {
      // Clear options when dialog closes to ensure fresh data on next open
      setCompanyOptions([]);
    }
  }, [createDialogOpen]);

  const createMutation = useMutation(createTierUpgrade, {
    onSuccess: () => {
      queryClient.invalidateQueries('tierUpgrades');
      queryClient.invalidateQueries('tierUpgradeStats');
      setCreateDialogOpen(false);
      resetForm();
      setFeedback({ type: 'success', message: 'Temporary tier upgrade granted successfully.' });
    },
    onError: (mutationError) => {
      setFeedback({
        type: 'error',
        message: mutationError?.message || 'Failed to grant temporary tier upgrade'
      });
    }
  });

  const cancelMutation = useMutation(cancelTierUpgrade, {
    onSuccess: () => {
      queryClient.invalidateQueries('tierUpgrades');
      queryClient.invalidateQueries('tierUpgradeStats');
      setCancelDialogOpen(false);
      setFeedback({ type: 'success', message: 'Tier upgrade cancelled successfully.' });
    },
    onError: (mutationError) => {
      setFeedback({
        type: 'error',
        message: mutationError?.message || 'Failed to cancel tier upgrade'
      });
    }
  });

  const extendMutation = useMutation(extendTierUpgrade, {
    onSuccess: () => {
      queryClient.invalidateQueries('tierUpgrades');
      setExtendDialogOpen(false);
      setFeedback({ type: 'success', message: 'Tier upgrade extended successfully.' });
    },
    onError: (mutationError) => {
      setFeedback({
        type: 'error',
        message: mutationError?.message || 'Failed to extend tier upgrade'
      });
    }
  });

  const resetForm = () => {
    setFormData({ companyId: '', companyName: '', upgradedTier: 'professional', durationDays: 30, reason: '' });
  };

  const handleCompanySearch = (value) => {
    setCompanySearch(value);
    // The query will automatically refetch due to the queryKey including companySearch
    // But we can also manually trigger a refetch if needed
    if (createDialogOpen) {
      refetchCompanies();
    }
  };

  const getStatusColor = (status) => {
    const colors = { active: 'success', expired: 'default', cancelled: 'error', converted: 'primary' };
    return colors[status] || 'default';
  };

  const getTierColor = (tier) => {
    const colors = { free: 'default', professional: 'primary', enterprise: 'secondary' };
    return colors[tier] || 'default';
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.message || 'Error loading tier upgrades'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {feedback.message && (
        <Alert
          severity={feedback.type || 'info'}
          sx={{ mb: 2 }}
          onClose={() => setFeedback({ type: '', message: '' })}
        >
          {feedback.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              setFeedback({ type: '', message: '' });
              refetch();
            }}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            Grant Upgrade
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {statsData?.stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}><Card><CardContent><Typography variant="h4" color="success.main">{statsData.stats.activeCount}</Typography><Typography variant="caption">Active</Typography></CardContent></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card><CardContent><Typography variant="h4">{statsData.stats.expiredCount}</Typography><Typography variant="caption">Expired</Typography></CardContent></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card><CardContent><Typography variant="h4" color="primary.main">{statsData.stats.convertedCount}</Typography><Typography variant="caption">Converted</Typography></CardContent></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card><CardContent><Typography variant="h4" color="error.main">{statsData.stats.cancelledCount}</Typography><Typography variant="caption">Cancelled</Typography></CardContent></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card><CardContent><Typography variant="h4" color="warning.main">{statsData.stats.expiringSoonCount}</Typography><Typography variant="caption">Expiring Soon</Typography></CardContent></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card><CardContent><Typography variant="h4">{statsData.stats.conversionRate}%</Typography><Typography variant="caption">Conversion Rate</Typography></CardContent></Card></Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="converted">Converted</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Upgrades Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Account No.</TableCell>
              <TableCell>Original Tier</TableCell>
              <TableCell>Upgraded Tier</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Granted By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.upgrades?.map((upgrade) => (
              <TableRow key={upgrade._id}>
                <TableCell>{upgrade.companyName || upgrade.companyId}</TableCell>
                <TableCell>
                  {upgrade.accountNumber ? (
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{upgrade.accountNumber}</Typography>
                  ) : (
                    <Typography variant="caption" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell><Chip size="small" label={upgrade.originalTier} color={getTierColor(upgrade.originalTier)} /></TableCell>
                <TableCell><Chip size="small" label={upgrade.upgradedTier} color={getTierColor(upgrade.upgradedTier)} /></TableCell>
                <TableCell>
                  <Typography variant="body2">{format(new Date(upgrade.startDate), 'MMM d')} - {format(new Date(upgrade.endDate), 'MMM d, yyyy')}</Typography>
                  {upgrade.status === 'active' && <Typography variant="caption" color="textSecondary">Expires {formatDistanceToNow(new Date(upgrade.endDate), { addSuffix: true })}</Typography>}
                </TableCell>
                <TableCell><Chip size="small" label={upgrade.status} color={getStatusColor(upgrade.status)} /></TableCell>
                <TableCell>{upgrade.grantedByName}</TableCell>
                <TableCell>
                  {upgrade.status === 'active' && (
                    <>
                      <Tooltip title="Extend">
                        <IconButton size="small" color="primary" onClick={() => { setSelectedUpgrade(upgrade); setExtendDialogOpen(true); }}>
                          <ExtensionIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error" onClick={() => { setSelectedUpgrade(upgrade); setCancelDialogOpen(true); }}>
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data?.pagination?.total || 0} page={page} onPageChange={(e, newPage) => setPage(newPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Temporary Tier Upgrade</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={companyOptions}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.companyId === value?.companyId}
                onOpen={() => {
                  // Reload companies when dropdown opens if not already loaded
                  if (companyOptions.length === 0 && !isSearchingCompanies) {
                    refetchCompanies();
                  }
                }}
                onInputChange={(e, value, reason) => {
                  // Only search on user input, not on option selection
                  if (reason === 'input') {
                    handleCompanySearch(value);
                  }
                }}
                onChange={(e, value) => setFormData({...formData, companyId: value?.companyId || '', companyName: value?.name || ''})}
                loading={isSearchingCompanies}
                noOptionsText={isSearchingCompanies ? 'Loading companies...' : 'No companies found'}
                filterOptions={(x) => x} // Disable client-side filtering since we filter server-side
                renderOption={(props, option) => (
                  <li {...props} key={option.companyId}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{option.name}</Typography>
                        {option.accountNumber && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: 'action.hover', padding: '1px 4px', borderRadius: 0.5, fontWeight: 500 }}>
                            {option.accountNumber}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {option.currentTier} • {option.businessType || 'General'}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    placeholder="Select or search for a company..."
                    helperText="Only Free and Professional tier companies are shown (Enterprise cannot be upgraded)"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearchingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Upgrade To</InputLabel>
                <Select value={formData.upgradedTier} label="Upgrade To" onChange={(e) => setFormData({...formData, upgradedTier: e.target.value})}>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Duration (days)" value={formData.durationDays} onChange={(e) => setFormData({...formData, durationDays: parseInt(e.target.value) || 1})} inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Reason" value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} placeholder="Why is this upgrade being granted?" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => createMutation.mutate(formData)} disabled={createMutation.isLoading || !formData.companyId || !formData.reason}>
            {createMutation.isLoading ? <CircularProgress size={24} /> : 'Grant Upgrade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Tier Upgrade</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Are you sure you want to cancel the upgrade for {selectedUpgrade?.companyName}?</Typography>
          <TextField fullWidth label="Reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
          <Button color="error" onClick={() => cancelMutation.mutate({ id: selectedUpgrade?._id, reason: cancelReason })}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Extend Dialog */}
      <Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)}>
        <DialogTitle>Extend Tier Upgrade</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Extend upgrade for {selectedUpgrade?.companyName}</Typography>
          <TextField fullWidth type="number" label="Additional Days" value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)} inputProps={{ min: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => extendMutation.mutate({ id: selectedUpgrade?._id, additionalDays: extendDays })}>Extend</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TierUpgrades;

